import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { NationBadge } from "@/components/Compliance";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Users, PoundSterling, TrendingUp, Building2, Megaphone, Save } from "lucide-react";
import PartnerSettings from "@/components/PartnerSettings";
import ReviewSettings from "@/components/ReviewSettings";
import StripeStatus from "@/components/StripeStatus";
import ProvisionUser from "@/components/ProvisionUser";

export default function Admin() {
  const [metrics, setMetrics] = useState(null);
  const [packs, setPacks] = useState([]);
  const [activeNation, setActiveNation] = useState("England");
  const [law, setLaw] = useState({ nation: "England", title: "", body: "", effective_date: "", source_link: "" });
  const [addRule, setAddRule] = useState(false);
  const [rule, setRule] = useState({ name: "", category: "documents", applies_to: "both", frequency_months: "", validity_label: "", explanation: "" });

  const loadPacks = () => api.get("/admin/rulepacks").then((r) => setPacks(r.data)).catch(() => {});
  useEffect(() => { api.get("/admin/metrics").then((r) => setMetrics(r.data)).catch(() => {}); loadPacks(); }, []);

  const pack = packs.find((p) => p.nation === activeNation);
  const maxNation = metrics ? Math.max(1, ...Object.values(metrics.nation_split)) : 1;

  const updateRule = (idx, key, val) => {
    setPacks((prev) => prev.map((p) => p.nation !== activeNation ? p : { ...p, rules: p.rules.map((r, i) => i === idx ? { ...r, [key]: val } : r) }));
  };

  const savePack = async () => {
    await api.put(`/admin/rulepacks/${activeNation}`, { rules: pack.rules });
    toast.success(`${activeNation} rule pack saved`);
  };

  const sendLaw = async () => {
    if (!law.title || !law.body) return toast.error("Add a title and message");
    const payload = { ...law, effective_date: law.effective_date || null, rule: addRule && rule.name ? { ...rule, frequency_months: rule.frequency_months ? Number(rule.frequency_months) : null } : null };
    const { data } = await api.post("/admin/lawupdate", payload);
    toast.success(`Radar live · ${data.affected_users} ${law.nation} landlords notified${data.update.rule ? " · checklist item added" : ""}`);
    setLaw({ ...law, title: "", body: "", effective_date: "", source_link: "" }); setAddRule(false); setRule({ ...rule, name: "", validity_label: "", explanation: "", frequency_months: "" }); loadPacks();
  };

  return (
    <Layout>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-6">Admin panel</h1>

      {metrics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Metric icon={Users} label="Total users" value={metrics.total_users} sub={`${metrics.trial_users} on trial`} />
          <Metric icon={PoundSterling} label="MRR" value={`£${metrics.mrr}`} sub={`${metrics.paid_users} paying`} />
          <Metric icon={TrendingUp} label="Trial conversion" value={`${metrics.trial_conversion}%`} sub="trial → paid" />
          <Metric icon={Building2} label="Properties" value={metrics.property_count} sub={`${metrics.certificate_count} certificates`} />
        </div>
      )}

      <Tabs defaultValue="overview">
        <TabsList className="bg-secondary">
          <TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="rules" data-testid="admin-tab-rules">Rule-pack editor</TabsTrigger>
          <TabsTrigger value="law" data-testid="admin-tab-law">Law-update tool</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          {metrics && (
            <div className="bg-card border border-border rounded-2xl p-6">
              <h3 className="font-heading font-bold mb-4">Nation split</h3>
              <div className="space-y-3">
                {Object.entries(metrics.nation_split).map(([n, c]) => (
                  <div key={n} className="flex items-center gap-3" data-testid={`split-${n.toLowerCase()}`}>
                    <div className="w-20"><NationBadge nation={n} /></div>
                    <div className="flex-1 h-6 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-emerald-brand rounded-full transition-all" style={{ width: `${(c / maxNation) * 100}%` }} /></div>
                    <span className="w-8 text-right font-semibold text-sm">{c}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="rules" className="mt-4">
          <div className="flex items-center gap-2 mb-4">
            {packs.map((p) => (
              <button key={p.nation} data-testid={`rulepack-tab-${p.nation.toLowerCase()}`} onClick={() => setActiveNation(p.nation)} className={`px-3 min-h-[40px] rounded-lg text-sm font-medium transition-colors ${activeNation === p.nation ? "bg-navy text-white" : "bg-secondary"}`}>{p.nation}</button>
            ))}
            <button data-testid="save-rulepack" onClick={savePack} className="ml-auto min-h-[40px] px-4 inline-flex items-center gap-2 rounded-lg bg-emerald-brand text-white font-semibold transition-colors"><Save size={15} /> Save</button>
          </div>
          <div className="space-y-2">
            {pack?.rules.map((r, i) => (
              <div key={r.id} data-testid={`rule-row-${r.id}`} className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <input value={r.name} onChange={(e) => updateRule(i, "name", e.target.value)} className="flex-1 font-semibold bg-transparent border-b border-transparent focus:border-border outline-none" />
                  <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={r.enabled} onChange={(e) => updateRule(i, "enabled", e.target.checked)} data-testid={`rule-enabled-${r.id}`} /> enabled</label>
                  {r.module && <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-800">module</span>}
                  {r.verify && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">verify source</span>}
                </div>
                <div className="grid sm:grid-cols-3 gap-2 mt-2 text-xs">
                  <select value={r.applies_to} onChange={(e) => updateRule(i, "applies_to", e.target.value)} className="px-2 py-1.5 rounded border border-input bg-card">
                    <option value="both">Single + HMO</option><option value="single">Single let</option><option value="hmo">HMO only</option>
                  </select>
                  <input value={r.validity_label || ""} onChange={(e) => updateRule(i, "validity_label", e.target.value)} placeholder="Validity" className="px-2 py-1.5 rounded border border-input bg-card" />
                  <input value={r.source_link || ""} onChange={(e) => updateRule(i, "source_link", e.target.value)} placeholder="Source link" className="px-2 py-1.5 rounded border border-input bg-card" />
                </div>
                <textarea value={r.explanation || ""} onChange={(e) => updateRule(i, "explanation", e.target.value)} rows={2} className="mt-2 w-full text-xs px-2 py-1.5 rounded border border-input bg-card" />
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="law" className="mt-4">
          <div className="bg-card border border-border rounded-2xl p-6 max-w-lg">
            <h3 className="font-heading font-bold flex items-center gap-2 mb-4"><Megaphone size={18} className="text-amber-500" /> Push a "What's changed" update</h3>
            <p className="text-sm text-muted-foreground mb-4">Appears on the Law change radar and emails affected landlords in the chosen nation only. Optionally adds a checklist item to every property there.</p>
            <select data-testid="law-nation" value={law.nation} onChange={(e) => setLaw({ ...law, nation: e.target.value })} className="w-full min-h-[44px] px-4 rounded-xl border border-input bg-card mb-3">
              {["England", "Wales", "Scotland"].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <input data-testid="law-title" value={law.title} onChange={(e) => setLaw({ ...law, title: e.target.value })} placeholder="Headline (e.g. Renters' Rights Act commences)" className="w-full min-h-[44px] px-4 rounded-xl border border-input bg-card mb-3" />
            <textarea data-testid="law-body" value={law.body} onChange={(e) => setLaw({ ...law, body: e.target.value })} rows={3} placeholder="What changed and what landlords need to do" className="w-full px-4 py-2 rounded-xl border border-input bg-card mb-3" />
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input data-testid="law-effective" type="date" value={law.effective_date} onChange={(e) => setLaw({ ...law, effective_date: e.target.value })} className="w-full min-h-[44px] px-4 rounded-xl border border-input bg-card" />
              <input data-testid="law-source" value={law.source_link} onChange={(e) => setLaw({ ...law, source_link: e.target.value })} placeholder="Official source URL" className="w-full min-h-[44px] px-4 rounded-xl border border-input bg-card" />
            </div>
            <label className="flex items-center gap-2 text-sm mb-3"><input data-testid="law-add-rule" type="checkbox" checked={addRule} onChange={(e) => setAddRule(e.target.checked)} /> Also add a new checklist item to every {law.nation} property</label>
            {addRule && (
              <div className="space-y-2 mb-3 p-3 rounded-xl bg-secondary/60" data-testid="law-rule-form">
                <input data-testid="law-rule-name" value={rule.name} onChange={(e) => setRule({ ...rule, name: e.target.value })} placeholder="Checklist item name (e.g. Landlord Database registration)" className="w-full min-h-[40px] px-3 rounded-lg border border-input bg-card" />
                <div className="grid grid-cols-2 gap-2">
                  <select data-testid="law-rule-category" value={rule.category} onChange={(e) => setRule({ ...rule, category: e.target.value })} className="min-h-[40px] px-3 rounded-lg border border-input bg-card">
                    {["documents", "registration", "licence", "gas", "electrical", "epc", "alarms", "deposit"].map((c) => <option key={c}>{c}</option>)}
                  </select>
                  <select data-testid="law-rule-applies" value={rule.applies_to} onChange={(e) => setRule({ ...rule, applies_to: e.target.value })} className="min-h-[40px] px-3 rounded-lg border border-input bg-card"><option value="both">Single + HMO</option><option value="single">Single let</option><option value="hmo">HMO only</option></select>
                  <input data-testid="law-rule-freq" type="number" value={rule.frequency_months} onChange={(e) => setRule({ ...rule, frequency_months: e.target.value })} placeholder="Renew every N months (blank = record)" className="min-h-[40px] px-3 rounded-lg border border-input bg-card" />
                  <input data-testid="law-rule-validity" value={rule.validity_label} onChange={(e) => setRule({ ...rule, validity_label: e.target.value })} placeholder="Validity label" className="min-h-[40px] px-3 rounded-lg border border-input bg-card" />
                </div>
                <textarea data-testid="law-rule-explanation" value={rule.explanation} onChange={(e) => setRule({ ...rule, explanation: e.target.value })} rows={2} placeholder="Explanation shown on the checklist (defaults to the message above)" className="w-full px-3 py-2 rounded-lg border border-input bg-card text-sm" />
              </div>
            )}
            <button data-testid="law-send" onClick={sendLaw} className="w-full min-h-[48px] rounded-xl bg-navy hover:bg-navy-700 text-white font-semibold transition-colors">Publish banner & notify</button>
          </div>
        </TabsContent>
      </Tabs>
      <div className="mt-6"><ProvisionUser /></div>
      <div className="mt-6"><StripeStatus /></div>
      <div className="mt-6"><PartnerSettings /></div>
      <div className="mt-6"><ReviewSettings /></div>
    </Layout>
  );
}

function Metric({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <div className="flex items-center gap-2 text-muted-foreground text-xs font-semibold uppercase tracking-wider"><Icon size={14} /> {label}</div>
      <div className="mt-2 font-heading text-2xl font-extrabold">{value}</div>
      <div className="text-xs text-muted-foreground">{sub}</div>
    </div>
  );
}
